import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, ShieldCheck, Award, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Hero3D from "@/components/site/Hero3D";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Subtle radial light behind everything */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -right-24 w-[40rem] h-[40rem] sun-ring glow-anim" />
        <div className="absolute top-1/3 -left-32 w-[24rem] h-[24rem] rounded-full"
             style={{ background: "radial-gradient(circle, rgba(42,59,50,0.08), transparent 70%)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1D20]/5 border border-[#1A1D20]/10 text-xs text-[#1A1D20]/80">
            <Sparkles size={14} className="text-[#EAB308]" />
            Schweizer Premium-Solar. Aus einer Hand.
          </div>

          <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-[#1A1D20]">
            Die Energie<br />
            der <span className="relative inline-block">
              Zukunft.
              <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" fill="none">
                <path d="M2 7 Q 50 1 100 5 T 198 5" stroke="#EAB308" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
            <br />
            <span className="text-[#4B5563] font-medium">Schweizer Qualität.</span>
          </h1>

          <p className="mt-6 text-[#4B5563] text-base md:text-lg leading-relaxed max-w-xl">
            Photovoltaik, Stromspeicher, Wallboxen und E-Mobilität –
            geplant, installiert und gewartet von erfahrenen Solarprofis
            in der ganzen Schweiz.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              data-testid="hero-cta-calculator"
              className="bg-[#EAB308] hover:bg-[#CA9A06] text-[#1A1D20] rounded-full h-12 px-6 font-semibold shadow-[0_8px_30px_-8px_rgba(234,179,8,0.6)]"
            >
              <a href="#rechner">
                Solarrechner starten <ArrowUpRight size={18} />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-consult"
              className="rounded-full h-12 px-6 border-[#1A1D20]/15 text-[#1A1D20] hover:bg-[#1A1D20] hover:text-white"
            >
              <a href="#kontakt">Beratung anfordern</a>
            </Button>
          </div>

          <div
            data-testid="hero-social-proof"
            className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-4"
          >
            <div className="flex -space-x-3" aria-hidden="true">
              {["stimme-1", "stimme-2", "stimme-3"].map((n) => (
                <img
                  key={n}
                  src={`/images/${n}.webp`}
                  alt=""
                  width="512"
                  height="512"
                  loading="lazy"
                  decoding="async"
                  className="w-11 h-11 rounded-full object-cover border-2 border-[#FDFCF9] img-placeholder"
                />
              ))}
              <span className="w-11 h-11 rounded-full bg-[#1A1D20] text-white border-2 border-[#FDFCF9] flex items-center justify-center text-[11px] font-semibold">
                850+
              </span>
            </div>

            <div className="text-sm">
              <span className="flex" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={15} className="text-[#EAB308] fill-[#EAB308]" />
                ))}
              </span>
              <span className="text-[#4B5563]">
                <strong className="text-[#1A1D20] font-semibold">4.9 / 5</strong> aus
                312 Bewertungen ·{" "}
                <a href="#stimmen" className="underline underline-offset-2 hover:text-[#1A1D20]">
                  Kundenstimmen lesen
                </a>
              </span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#1A1D20]/70">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#2A3B32]" aria-hidden="true" /> 25 Jahre Modulgarantie
            </span>
            <span className="inline-flex items-center gap-2">
              <Award size={16} className="text-[#2A3B32]" aria-hidden="true" /> Swissolar-zertifiziert
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-6"
        >
          <Hero3D />
        </motion.div>
      </div>
    </section>
  );
}
